import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertTriangle,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
} from "lucide-react";
import { DragConflict, ConflictChoice } from "@/stores/collaboration";
import { UserAvatarGroup } from "./UserAvatar";
import { Button } from "@/components/ui/button";

interface ConflictAlertProps {
  conflict: DragConflict;
  choice?: ConflictChoice;
  onResolve?: (
    conflictId: string,
    resolution: "merge" | "reject" | "queue",
  ) => void;
  onDismiss?: (conflictId: string) => void;
  className?: string;
}

export const ConflictAlert: React.FC<ConflictAlertProps> = ({
  conflict,
  choice,
  onResolve,
  onDismiss,
  className = "",
}) => {
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Countdown timer for auto-resolve
  useEffect(() => {
    if (choice?.autoResolveIn) {
      const startTime = choice.timestamp.getTime();
      const duration = choice.autoResolveIn * 1000;

      const updateTimer = () => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, duration - elapsed);
        setTimeLeft(remaining);

        if (remaining === 0) {
          clearInterval(interval);
          // Auto-resolve based on choice type
          if (choice.type === "automatic" && onResolve) {
            onResolve(conflict.id, "merge"); // Default auto-resolution
          }
        }
      };

      const interval = setInterval(updateTimer, 500);
      updateTimer();

      return () => clearInterval(interval);
    }
    return undefined;
  }, [choice, conflict.id, onResolve]);

  const getConflictIcon = () => {
    switch (conflict.status) {
      case "resolved":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "timeout":
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
    }
  };

  const getConflictTitle = () => {
    const userCount = conflict.users.length;
    const componentName = `Component ${conflict.componentId.slice(-6)}`;

    switch (conflict.status) {
      case "resolved":
        return `Conflict resolved - ${componentName}`;
      case "timeout":
        return `Conflict timeout - ${componentName}`;
      default:
        return `${userCount} users editing ${componentName}`;
    }
  };

  const getProgressColor = () => {
    if (!timeLeft || !choice?.autoResolveIn) return "bg-blue-500";

    const progress = timeLeft / (choice.autoResolveIn * 1000);
    if (progress > 0.6) return "bg-blue-500";
    if (progress > 0.3) return "bg-amber-500";
    return "bg-red-500";
  };

  const formatTimeLeft = (ms: number): string => {
    const seconds = Math.ceil(ms / 1000);
    return `${seconds}s`;
  };

  if (conflict.status === "resolved" && !isExpanded) {
    return (
      <motion.div
        className={`bg-green-50 border border-green-200 rounded-lg p-3 ${className}`}
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        layout
      >
        <div className="flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <div className="flex-1">
            <div className="text-sm font-medium text-green-900">
              Conflict resolved
            </div>
            <div className="text-xs text-green-700">
              Resolution: {conflict.resolution} • by{" "}
              {conflict.resolvedBy || "system"}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className={`bg-white border-2 border-amber-200 rounded-lg shadow-lg overflow-hidden ${className}`}
      initial={{ opacity: 0, scale: 0.95, y: -10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -10 }}
      layout
      role="alert"
      aria-live="polite"
      aria-label={`Conflict alert for ${getConflictTitle()}`}
    >
      {/* Progress bar for auto-resolve */}
      {timeLeft !== null && choice?.autoResolveIn && (
        <motion.div
          className={`h-1 ${getProgressColor()}`}
          initial={{ width: "100%" }}
          animate={{
            width: `${(timeLeft / (choice.autoResolveIn * 1000)) * 100}%`,
          }}
          transition={{ duration: 0.1, ease: "linear" }}
        />
      )}

      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            {getConflictIcon()}
            <div>
              <div className="text-sm font-medium text-gray-900">
                {getConflictTitle()}
              </div>
              <div className="text-xs text-gray-500">
                {new Date(conflict.timestamp).toLocaleTimeString()}
              </div>
            </div>
          </div>

          {/* Auto-resolve timer */}
          {timeLeft !== null && timeLeft > 0 && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" aria-hidden="true" />
              <span aria-live="polite">Auto-resolve in {formatTimeLeft(timeLeft)}</span>
            </div>
          )}

          {/* Dismiss button */}
          {onDismiss && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDismiss(conflict.id)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Users involved */}
        <div className="flex items-center gap-3 mb-4">
          <Users className="w-4 h-4 text-gray-400" />
          <UserAvatarGroup
            users={conflict.users}
            maxVisible={4}
            size="sm"
            showStatus={false}
            interactive={false}
          />
          <span className="text-xs text-gray-500">
            {conflict.users.map((u) => u.user?.username || 'Unknown').join(", ")}
          </span>
        </div>

        {/* Choice description */}
        {choice && (
          <div className="mb-4 p-3 bg-gray-50 rounded text-sm text-gray-700">
            {choice.description}
            {choice.type === "automatic" && (
              <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                <Loader className="w-3 h-3 animate-spin" />
                AI-powered automatic resolution
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        {conflict.status === "active" && onResolve && (
          <div className="space-y-2">
            {choice?.options ? (
              // Custom choice options
              <div className="flex flex-wrap gap-2">
                {choice.options.map((option, index) => (
                  <Button
                    key={index}
                    variant={option.variant === "primary" ? "default" : option.variant}
                    size="sm"
                    onClick={option.action}
                    className="flex-1 min-w-0"
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            ) : (
              // Default resolution options
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => onResolve(conflict.id, "merge")}
                  className="text-xs"
                >
                  Merge
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onResolve(conflict.id, "reject")}
                  className="text-xs"
                >
                  Keep Mine
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onResolve(conflict.id, "queue")}
                  className="text-xs"
                >
                  Queue
                </Button>
              </div>
            )}

            {/* Advanced options */}
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-xs text-gray-500"
              >
                {isExpanded ? "Less options" : "More options"}
              </Button>
            </div>

            {/* Expanded details */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t pt-3 space-y-2"
                >
                  <div className="text-xs text-gray-600">
                    <div className="font-medium mb-1">Resolution Options:</div>
                    <div className="space-y-1">
                      <div>
                        <strong>Merge:</strong> Combine all changes
                        automatically
                      </div>
                      <div>
                        <strong>Keep Mine:</strong> Discard other users' changes
                      </div>
                      <div>
                        <strong>Queue:</strong> Apply changes sequentially
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Conflict ID: {conflict.id.slice(-8)}</span>
                    <span>Component: {conflict.componentId.slice(-8)}</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Container for managing multiple conflict alerts
export const ConflictAlertsContainer: React.FC<{
  conflicts: DragConflict[];
  choices: ConflictChoice[];
  onResolve?: (
    conflictId: string,
    resolution: "merge" | "reject" | "queue",
  ) => void;
  onDismiss?: (conflictId: string) => void;
  maxVisible?: number;
  position?: "top-right" | "top-left" | "bottom-right" | "bottom-left";
  className?: string;
}> = ({
  conflicts,
  choices,
  onResolve,
  onDismiss,
  maxVisible = 3,
  position = "top-right",
  className = "",
}) => {
  const activeConflicts = conflicts
    .filter((conflict) => conflict.status === "active")
    .slice(0, maxVisible);

  const positionClasses = {
    "top-right": "top-20 right-4",
    "top-left": "top-20 left-4",
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
  };

  if (activeConflicts.length === 0) return null;

  return (
    <div
      className={`fixed ${positionClasses[position]} z-50 w-80 max-w-sm ${className}`}
    >
      <AnimatePresence mode="popLayout">
        <div className="space-y-3">
          {activeConflicts.map((conflict) => {
            const choice = choices.find((c) => c.conflictId === conflict.id);

            return (
              <ConflictAlert
                key={conflict.id}
                conflict={conflict}
                choice={choice}
                onResolve={onResolve}
                onDismiss={onDismiss}
              />
            );
          })}

          {/* Show count if there are more conflicts */}
          {conflicts.length > maxVisible && (
            <motion.div
              className="bg-gray-100 border border-gray-200 rounded-lg p-2 text-center text-sm text-gray-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              +{conflicts.length - maxVisible} more conflicts
            </motion.div>
          )}
        </div>
      </AnimatePresence>
    </div>
  );
};
